import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDoctorForm } from '../hooks/useDoctorForm';
import apiClient from '../../../services/apiClient';
import Spinner from '../../../components/ui/Spinner';

interface Specialty {
  id: number;
  name: string;
}

const DoctorFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const doctorId = id ? parseInt(id) : undefined;
  const navigate = useNavigate();

  const {
    formData,
    errors,
    loading: initialLoading,
    saving,
    handleSubmit,
    handleInputChange,
    resetForm,
  } = useDoctorForm(doctorId);

  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(true);

  useEffect(() => {
    const loadSpecialties = async () => {
      try {
        const response = await apiClient.get<any>('/settings/specialties');
        setSpecialties(response.data?.data || []);
      } catch (err) {
        console.error('Error loading specialties:', err);
      } finally {
        setLoadingSpecialties(false);
      }
    };

    loadSpecialties();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    const success = await handleSubmit(e);
    if (success) {
      navigate('/doctors');
    }
  };

  if (initialLoading || loadingSpecialties) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 text-center">
            <Spinner variant="full-page" text="Cargando..." size="lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fa fa-user-md"></i> {doctorId ? 'Editar' : 'Nuevo'} Médico
              </h3>
              <div className="card-tools">
                <Link to="/doctors" className="btn btn-outline-secondary btn-sm">
                  <i className="fa fa-arrow-left"></i> Volver
                </Link>
              </div>
            </div>

            <div className="card-body">
              <form onSubmit={onSubmit}>
                <fieldset>
                  <legend>Datos Personales</legend>
                </fieldset>

                {/* First Name and Last Name */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">NOMBRE *</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      disabled={saving}
                      placeholder="Nombre"
                    />
                    {errors.first_name && <span className="text-danger">{errors.first_name}</span>}
                  </div>
                  <label className="col-sm-2 col-form-label">APELLIDO *</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      disabled={saving}
                      placeholder="Apellido"
                    />
                    {errors.last_name && <span className="text-danger">{errors.last_name}</span>}
                  </div>
                </div>

                {/* Specialty */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">ESPECIALIDAD *</label>
                  <div className="col-sm-4">
                    <select
                      className={`form-control ${errors.specialty_id ? 'is-invalid' : ''}`}
                      value={formData.specialty_id}
                      onChange={(e) => handleInputChange('specialty_id', parseInt(e.target.value))}
                      disabled={saving}
                    >
                      <option value={0}>Seleccionar</option>
                      {specialties.map((specialty) => (
                        <option key={specialty.id} value={specialty.id}>
                          {specialty.name}
                        </option>
                      ))}
                    </select>
                    {errors.specialty_id && <span className="text-danger">{errors.specialty_id}</span>}
                  </div>
                  <label className="col-sm-2 col-form-label">CÉDULA PROFESIONAL</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.license_number || ''}
                      onChange={(e) => handleInputChange('license_number', e.target.value)}
                      disabled={saving}
                      placeholder="Número de licencia"
                    />
                  </div>
                </div>

                <fieldset>
                  <legend>Credenciales de Acceso</legend>
                </fieldset>

                {/* Username and Email */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">USUARIO</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.username || ''}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      disabled={saving}
                      placeholder="Nombre de usuario"
                    />
                    <small className="text-muted">Se genera automáticamente si se deja vacío</small>
                  </div>
                  <label className="col-sm-2 col-form-label">CORREO</label>
                  <div className="col-sm-4">
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      value={formData.email || ''}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      disabled={saving}
                      placeholder="correo@ejemplo.com"
                    />
                    {errors.email && <span className="text-danger">{errors.email}</span>}
                  </div>
                </div>

                {/* Password */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">
                    {doctorId ? 'NUEVA CONTRASEÑA' : 'CONTRASEÑA'}
                  </label>
                  <div className="col-sm-4">
                    <input
                      type="password"
                      className="form-control"
                      value={formData.password || ''}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      disabled={saving}
                      placeholder={doctorId ? 'Dejar vacío para mantener actual' : 'Mínimo 6 caracteres'}
                    />
                  </div>
                </div>

                <fieldset>
                  <legend>Información de Contacto</legend>
                </fieldset>

                {/* Contact */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">TELÉFONO</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.phone || ''}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      disabled={saving}
                      placeholder="(809) 000-0000"
                    />
                  </div>
                  <label className="col-sm-2 col-form-label">CELULAR</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.mobile || ''}
                      onChange={(e) => handleInputChange('mobile', e.target.value)}
                      disabled={saving}
                      placeholder="(829) 000-0000"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-2 col-form-label">WhatsApp</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.whatsapp || ''}
                      onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                      disabled={saving}
                      placeholder="(829) 000-0000"
                    />
                  </div>
                  <label className="col-sm-2 col-form-label">EXTENSIÓN</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.extension || ''}
                      onChange={(e) => handleInputChange('extension', e.target.value)}
                      disabled={saving}
                      placeholder="123"
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">DIRECCIÓN</label>
                  <div className="col-sm-10">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.address || ''}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      disabled={saving}
                      placeholder="Dirección completa"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-2 col-form-label">CIUDAD</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.city || ''}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      disabled={saving}
                      placeholder="Ciudad"
                    />
                  </div>
                  <label className="col-sm-2 col-form-label">PAÍS</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.country || ''}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      disabled={saving}
                      placeholder="País"
                    />
                  </div>
                </div>

                <fieldset>
                  <legend>Información Adicional</legend>
                </fieldset>

                {/* Date of Birth and Gender */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">FECHA DE NACIMIENTO</label>
                  <div className="col-sm-4">
                    <input
                      type="date"
                      className="form-control"
                      value={formData.date_of_birth || ''}
                      onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <label className="col-sm-2 col-form-label">GÉNERO</label>
                  <div className="col-sm-4">
                    <select
                      className="form-control"
                      value={formData.gender || ''}
                      onChange={(e) => handleInputChange('gender', e.target.value || undefined)}
                      disabled={saving}
                    >
                      <option value="">Seleccionar</option>
                      <option value="male">Masculino</option>
                      <option value="female">Femenino</option>
                      <option value="other">Otro</option>
                    </select>
                  </div>
                </div>

                {/* Education and Designation */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">EDUCACIÓN</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.education || ''}
                      onChange={(e) => handleInputChange('education', e.target.value)}
                      disabled={saving}
                      placeholder="Universidad / Formación"
                    />
                  </div>
                  <label className="col-sm-2 col-form-label">CARGO</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.designation || ''}
                      onChange={(e) => handleInputChange('designation', e.target.value)}
                      disabled={saving}
                      placeholder="Ej: Jefe de Cardiología"
                    />
                  </div>
                </div>

                {/* Consultation Fee */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">HONORARIOS CONSULTA</label>
                  <div className="col-sm-4">
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        value={formData.consultation_fee || ''}
                        onChange={(e) => handleInputChange('consultation_fee', e.target.value ? parseFloat(e.target.value) : undefined)}
                        disabled={saving}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">OBSERVACIONES</label>
                  <div className="col-sm-10">
                    <textarea
                      className="form-control"
                      rows={3}
                      value={formData.notes || ''}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      disabled={saving}
                      placeholder="Notas adicionales sobre el médico"
                    />
                  </div>
                </div>

                <fieldset>
                  <legend>Información Fiscal</legend>
                </fieldset>

                {/* Professional RNC and Bills Independently */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">RNC PROFESIONAL</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className={`form-control ${errors.professional_rnc ? 'is-invalid' : ''}`}
                      value={formData.professional_rnc || ''}
                      onChange={(e) => handleInputChange('professional_rnc', e.target.value)}
                      disabled={saving}
                      placeholder="RNC para facturación independiente"
                      maxLength={11}
                    />
                    {errors.professional_rnc && <span className="text-danger">{errors.professional_rnc}</span>}
                    <small className="form-text text-muted d-block">9 u 11 dígitos. Déjelo vacío para usar el RNC de la clínica.</small>
                  </div>
                  <label className="col-sm-2 col-form-label">FACTURA INDEP.</label>
                  <div className="col-sm-4">
                    <div className="form-check mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={formData.bills_independently || false}
                        onChange={(e) => handleInputChange('bills_independently', e.target.checked)}
                        disabled={saving}
                        id="bills_independently"
                      />
                      <label className="form-check-label" htmlFor="bills_independently">
                        Emite facturas con su propio RNC
                      </label>
                    </div>
                  </div>
                </div>

                <fieldset>
                  <legend>Estado</legend>
                </fieldset>

                {/* Active and Available for Appointments */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">ESTADO</label>
                  <div className="col-sm-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      disabled={saving}
                    />
                    <span style={{ marginLeft: '10px' }}>Médico activo</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-2 col-form-label">DISPONIBLE CITAS</label>
                  <div className="col-sm-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={formData.is_available_for_appointments}
                      onChange={(e) => handleInputChange('is_available_for_appointments', e.target.checked)}
                      disabled={saving}
                    />
                    <span style={{ marginLeft: '10px' }}>Disponible para atender citas</span>
                  </div>
                </div>

                {/* Buttons */}
                <div className="form-group">
                  <div className="offset-sm-2 col-sm-10">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <i className="fa fa-spinner fa-spin"></i> Guardando...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-save"></i> Guardar
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={resetForm}
                      disabled={saving}
                      style={{ marginLeft: '10px' }}
                    >
                      <i className="fa fa-refresh"></i> Limpiar
                    </button>
                    <Link
                      to="/doctors"
                      className="btn btn-outline-secondary"
                      style={{ marginLeft: '10px' }}
                    >
                      Cancelar
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorFormPage;
